import { get } from "lodash";
import {
  FormEvent,
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { Icon } from "@shared/ui";

import type { AxleAiModel } from "@platform/automatic-api";

import { ClientContext } from "../../App";
import Logo from "../../components/Logo";
import AxleAiActionCard, {
  type ConfirmOptions,
} from "../../components/internal/AxleAiActionCard";
import AxleAiMarkdown from "../../components/internal/AxleAiMarkdown";
import Add from "../../icons/Add";
import {
  chatWithAxleAi,
  createIgnitionSession,
  createUser,
  getAxleAiChat,
  listAxleAiChats,
  saveAxleAiChat,
  sendSupportEmail,
  updateClientsConfig,
} from "../../lib/automatic";
import {
  activeChatIdFor,
  chatTitle,
  newChatId,
  rememberActiveChat,
} from "../../lib/axleAi/chats";
import { runLookup } from "../../lib/axleAi/lookups";
import {
  AXLE_AI_MODEL_OPTIONS,
  chatModel,
  rememberModel,
  rememberedModel,
} from "../../lib/axleAi/models";
import type {
  AxleAiChat,
  AxleAiChatSummary,
  AxleAiMessage,
  ConfirmAction,
  PendingAction,
} from "../../lib/axleAi/types";
import { appendClientIdToPath, getErrorMessage } from "../../lib/utility";

// Tool results are the Claude API's bookkeeping, not something the user said.
const isToolResultTurn = (message: AxleAiMessage) =>
  Array.isArray(message.content) &&
  message.content.every((block) => block.type === "tool_result");

const messageText = (message: AxleAiMessage) =>
  typeof message.content === "string"
    ? message.content
    : message.content
        .filter((block) => block.type === "text")
        .map((block) => block.text)
        .join("\n\n");

const toolResult = (
  action: PendingAction,
  content: unknown,
  isError = false
): AxleAiMessage => ({
  role: "user",
  content: [
    {
      type: "tool_result",
      tool_use_id: action.toolUseId,
      content: JSON.stringify(content),
      is_error: isError,
    },
  ],
});

// Runs a confirmed action through the same dashboard calls its own page uses,
// and returns what Claude reads back as the outcome.
const applyAction = async (
  action: ConfirmAction,
  clientId: string,
  { templateId, email }: ConfirmOptions = {}
) => {
  if (action.type === "email_support") {
    const { topic, subject, body } = action;
    return sendSupportEmail(clientId, { topic, subject, body, ...email });
  }

  if (action.type === "update_client_config") {
    const [data, error] = await updateClientsConfig(
      clientId,
      action.config,
      action.displayName,
      action.clientId
    );
    if (error) throw new Error(error.message);
    return data;
  }

  const user = await createUser(action.user, clientId);
  const [session, error] = await createIgnitionSession(
    user.data.id,
    clientId,
    templateId
  );
  if (error) throw new Error(error.message);
  return {
    ...session,
    dashboardPath: appendClientIdToPath(
      `/ignition/${session.ignitionToken}`,
      clientId
    ),
  };
};

// What went wrong with a chat request, in terms the user can act on.
const chatErrorMessage = (err: unknown) => {
  const status = (err as { status?: number })?.status;
  if (status === 504 || status === 408) {
    return "Axle AI took too long to answer. Try again, or ask about fewer records at once.";
  }
  if (err instanceof TypeError) {
    return "Couldn't reach Axle AI. Check your connection and try again.";
  }
  return getErrorMessage(err, "Axle AI couldn't respond. Try again.");
};

const lookupResult = async (
  lookup: Extract<PendingAction, { type: "lookup" }>,
  clientId: string
) => {
  try {
    return toolResult(lookup, await runLookup(lookup, clientId));
  } catch (err) {
    return toolResult(lookup, getErrorMessage(err, "Lookup failed"), true);
  }
};

// One example per thing Axle AI does; picking one puts it in the composer to
// edit or send, since some need details filled in.
const SUGGESTIONS = [
  { label: "Policies", prompt: "Find inactive policies created last month" },
  {
    label: "Ignition sessions",
    prompt: "How many ignition sessions were created this week, by status?",
  },
  { label: "Client config", prompt: "Show this client's notification config" },
  {
    label: "New ignition session",
    prompt: "Start an ignition session for ",
  },
  { label: "Axle docs", prompt: "How does policy monitoring work?" },
  {
    label: "Email support",
    prompt: "Email Axle support about a config issue: ",
  },
];

// The draft lives in the page, so a question that fails to send is put back
// for the user instead of vanishing.
type ComposerProps = {
  inputRef?: RefObject<HTMLInputElement>;
  model: AxleAiModel;
  onModelChange: (model: AxleAiModel) => void;
  text: string;
  onChange: (text: string) => void;
  onSubmit: (text: string) => void;
  disabled: boolean;
  placeholder: string;
};

const Composer = ({
  inputRef,
  model,
  onModelChange,
  text,
  onChange,
  onSubmit,
  disabled,
  placeholder,
}: ComposerProps) => {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex items-center gap-x-2 w-full border border-gray-300 rounded-lg bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-black focus-within:border-transparent"
    >
      <input
        ref={inputRef}
        type="text"
        autoFocus
        className="flex-1 text-sm py-1 focus:outline-none bg-transparent"
        placeholder={placeholder}
        value={text}
        onChange={(e) => onChange(e.target.value)}
      />
      <select
        aria-label="Model"
        value={model}
        onChange={(e) => onModelChange(e.target.value as AxleAiModel)}
        disabled={disabled}
        className="text-sm text-gray-500 bg-transparent py-1 pr-1 focus:outline-none cursor-pointer disabled:cursor-not-allowed"
      >
        {AXLE_AI_MODEL_OPTIONS.map(({ id, label }) => (
          <option key={id} value={id}>
            {label}
          </option>
        ))}
      </select>
      <button
        type="submit"
        disabled={disabled || !text.trim()}
        className="px-4 py-1.5 bg-black text-white text-sm font-medium rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
      >
        Submit
      </button>
    </form>
  );
};

type ChatListProps = {
  chats: AxleAiChatSummary[];
  activeChatId?: string;
  onNew: () => void;
  onOpen: (chat: AxleAiChatSummary) => void;
  disabled: boolean;
};

const ChatList = ({
  chats,
  activeChatId,
  onNew,
  onOpen,
  disabled,
}: ChatListProps) => (
  <div className="w-64 shrink-0 border-r border-gray-200 flex flex-col gap-y-2 p-4 overflow-y-auto">
    <button
      onClick={onNew}
      disabled={disabled}
      className="flex items-center justify-center gap-x-2 px-4 py-2 border border-gray-300 bg-white text-sm font-medium rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
    >
      <Icon icon={Add} height="h-3" width="w-3" className="fill-black" />
      New Chat
    </button>
    {chats.length > 0 && (
      <p className="text-xs font-medium text-gray-400 px-2 pt-2">Chats</p>
    )}
    {chats.map((chat) => (
      <button
        key={chat.id}
        onClick={() => onOpen(chat)}
        disabled={disabled}
        title={chat.title}
        className={`text-left text-sm px-3 py-2 rounded truncate hover:bg-gray-100 disabled:cursor-not-allowed ${
          chat.id === activeChatId
            ? "bg-gray-100 text-black font-medium"
            : "text-gray-500"
        }`}
      >
        {chat.title}
      </button>
    ))}
  </div>
);

/**
 * Axle AI: a chat over Axle data for internal users. Claude can search
 * policies, accounts and clients, read the selected client's config, and
 * propose config updates and new ignition sessions, which only happen once the
 * user confirms them here. Chats are saved by automatic, per user and client.
 */
const AxleAi = () => {
  const { selectedClient: clientId, clientConfig } = useContext(ClientContext);
  const [messages, setMessages] = useState<AxleAiMessage[]>([]);
  const [pendingAction, setPendingAction] = useState<ConfirmAction>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chats, setChats] = useState<AxleAiChatSummary[]>([]);
  const [activeChatId, setActiveChatId] = useState<string>();
  const [draft, setDraft] = useState("");
  const [model, setModel] = useState(rememberedModel);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // No chat means a new one.
  const showChat = (chat?: AxleAiChat) => {
    setActiveChatId(chat?.id);
    setModel(chatModel(chat?.model));
    setMessages(chat?.messages ?? []);
    setPendingAction(chat?.pendingAction);
    setError(null);
  };

  // Applies from the next question, and is what new chats start with.
  const handleModelChange = (picked: AxleAiModel) => {
    setModel(picked);
    rememberModel(picked);
  };

  const startNewChat = () => {
    rememberActiveChat(clientId);
    showChat();
  };

  // The chat list only has titles, so opening a chat fetches it in full.
  const openChat = async (chatId: string) => {
    setLoading(true);
    try {
      showChat(await getAxleAiChat(clientId, chatId));
      rememberActiveChat(clientId, chatId);
    } catch (err) {
      setError(getErrorMessage(err, "Couldn't open this chat."));
    } finally {
      setLoading(false);
    }
  };

  // A chat belongs to one client, so a client shows its own chats, reopening
  // the one that was open when the user last left this page.
  useEffect(() => {
    if (!clientId) return;
    let current = true;
    const remembered = activeChatIdFor(clientId);
    showChat();
    setChats([]);

    listAxleAiChats(clientId)
      .then((clientChats) => {
        if (!current) return;
        setChats(clientChats);
        if (clientChats.some(({ id }) => id === remembered)) {
          openChat(remembered as string);
        }
      })
      .catch((err) => {
        if (current) {
          setError(getErrorMessage(err, "Couldn't load your saved chats."));
        }
      });

    return () => {
      current = false;
    };
  }, [clientId]);

  // Saved once a turn completes, so a stored chat always ends where Claude
  // last answered. The list updates straight away; a failed save only warns,
  // since the chat itself still works.
  const persist = (
    conversation: AxleAiMessage[],
    action: ConfirmAction | undefined
  ) => {
    const id = activeChatId ?? newChatId();
    const title = chatTitle(conversation);
    rememberActiveChat(clientId, id);
    setActiveChatId(id);
    setChats((current) => [
      { id, title, updatedAt: Date.now() },
      ...current.filter((chat) => chat.id !== id),
    ]);
    saveAxleAiChat(clientId, {
      id,
      title,
      model,
      messages: conversation,
      pendingAction: action,
    }).catch((err) =>
      setError(
        `This chat couldn't be saved: ${getErrorMessage(err, "request failed")}`
      )
    );
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [messages, loading]);

  // Sends the conversation plus one new user message. On failure the message
  // is dropped so the conversation still ends where Claude last answered, and
  // a typed question goes back into the composer.
  // Lookups Claude asks for run straight away, until it answers or proposes an
  // action to confirm; the backend caps how many one question can make.
  const send = async (next: AxleAiMessage) => {
    let conversation = [...messages, next];
    const previousAction = pendingAction;
    setMessages(conversation);
    setPendingAction(undefined);
    setError(null);
    setLoading(true);

    try {
      for (;;) {
        const turn = await chatWithAxleAi(clientId, conversation, model);
        conversation = [...conversation, ...turn.messages];
        setMessages(conversation);

        const action = turn.pendingAction;
        if (action?.type !== "lookup") {
          setPendingAction(action);
          persist(conversation, action);
          break;
        }
        conversation = [...conversation, await lookupResult(action, clientId)];
      }
    } catch (err) {
      setMessages(messages);
      setPendingAction(previousAction);
      if (typeof next.content === "string") setDraft(next.content);
      setError(chatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (
    action: ConfirmAction,
    options?: ConfirmOptions
  ) => {
    setLoading(true);
    let result: AxleAiMessage;
    try {
      result = toolResult(action, await applyAction(action, clientId, options));
    } catch (err) {
      result = toolResult(action, getErrorMessage(err, "Request failed"), true);
    }
    await send(result);
  };

  const handleCancel = (action: ConfirmAction) =>
    send(toolResult(action, "The user cancelled this."));

  const handleSuggestion = (prompt: string) => {
    setDraft(prompt);
    inputRef.current?.focus();
  };

  const handleAsk = (text: string) => {
    setDraft("");
    send({ role: "user", content: text });
  };

  const visibleMessages = messages.filter(
    (message) => !isToolResultTurn(message) && messageText(message)
  );

  const errorBanner = error && (
    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3">
      {error}
    </div>
  );

  const chatList = (
    <ChatList
      chats={chats}
      activeChatId={activeChatId}
      onNew={startNewChat}
      onOpen={(chat) => openChat(chat.id)}
      disabled={loading}
    />
  );

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 h-full min-h-0">
        {chatList}
        <div className="flex flex-col flex-1 min-w-0 items-center justify-center px-12 gap-y-8">
          <div className="flex flex-col items-center gap-y-3">
            <Logo h="48" />
            <h2 className="text-lg font-medium text-gray-900">
              What can I help with?
            </h2>
          </div>
          <div className="w-full max-w-2xl flex flex-col gap-y-4">
            <Composer
              inputRef={inputRef}
              model={model}
              onModelChange={handleModelChange}
              text={draft}
              onChange={setDraft}
              onSubmit={handleAsk}
              disabled={loading}
              placeholder="Ask Axle AI..."
            />
            {errorBanner}
            <div className="flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map(({ label, prompt }) => (
                <button
                  key={label}
                  onClick={() => handleSuggestion(prompt)}
                  disabled={loading}
                  title={prompt}
                  className="px-3 py-1.5 border border-gray-200 rounded-full text-sm text-gray-600 hover:bg-gray-50 hover:text-black disabled:opacity-40 transition-colors"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 h-full min-h-0">
      {chatList}
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex-1 overflow-y-auto px-12 py-10">
          <div className="max-w-3xl mx-auto flex flex-col gap-y-6">
            {visibleMessages.map((message, index) =>
              message.role === "user" ? (
                <div key={index} className="flex justify-end">
                  <div className="bg-gray-100 rounded-2xl px-4 py-2 text-sm max-w-[80%] whitespace-pre-wrap">
                    {messageText(message)}
                  </div>
                </div>
              ) : (
                <div key={index} className="flex gap-x-3">
                  <div className="shrink-0 pt-1">
                    <Logo h="12" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <AxleAiMarkdown text={messageText(message)} />
                  </div>
                </div>
              )
            )}

            {pendingAction && (
              <AxleAiActionCard
                action={pendingAction}
                clientId={clientId}
                clientName={get(clientConfig, "displayName", clientId)}
                onConfirm={(options) => handleConfirm(pendingAction, options)}
                onCancel={() => handleCancel(pendingAction)}
                disabled={loading}
              />
            )}

            {loading && <p className="text-sm text-gray-400">Thinking...</p>}

            {errorBanner}

            <div ref={bottomRef} />
          </div>
        </div>

        <div className="px-12 pb-6">
          <div className="max-w-3xl mx-auto">
            <Composer
              model={model}
              onModelChange={handleModelChange}
              text={draft}
              onChange={setDraft}
              onSubmit={handleAsk}
              disabled={loading || Boolean(pendingAction)}
              placeholder={
                pendingAction
                  ? "Confirm or cancel the card above first"
                  : "Ask a follow-up..."
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AxleAi;
